import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["currentPageIndex", "defaultCurrentPageIndex", "onPageSelected", "renderNextButton", "renderNumberButton", "renderPreviousButton", "totalPages"];
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import CustomPropTypes from '../../util/custom-prop-types';
import { IconChevronLeft, IconChevronRight } from '../Icon/index';
import PaginationButton from './PaginationButton';
import { StyledText } from '../Text/Text';
import getIndexRange from './pagination.getIndexRange';
import { PaginationMixin } from '../../mixins/components/PaginationMixin';
import PaginationList from './PaginationList';
import PaginationNumberItem from './PaginationNumberItem';
import PaginationJumpItem from './PaginationJumpItem';
import PaginationReadoutItem from './PaginationReadoutItem';
export var StyledPagination = /*#__PURE__*/styled.nav.withConfig({
  displayName: "StyledPagination",
  componentId: "c11n-8-64-0__sc-2vwigm-0"
})(["", ""], PaginationMixin); // CDS-537: Generate unique keys to prevent flickering.

var keyCounter = 1;

var getKey = function getKey() {
  keyCounter += 1;
  return keyCounter;
};
/**
 * Pagination is the division of a document into discrete pages. It is ideal for aiding
 * navigation and segmenting information into shorter, easier to understand blocks.
 *
 * @since 7.42.0
 */


var Pagination = function Pagination(props) {
  var currentPageIndex = props.currentPageIndex,
      defaultCurrentPageIndex = props.defaultCurrentPageIndex,
      onPageSelected = props.onPageSelected,
      renderNextButton = props.renderNextButton,
      renderNumberButton = props.renderNumberButton,
      renderPreviousButton = props.renderPreviousButton,
      totalPages = props.totalPages,
      rest = _objectWithoutProperties(props, _excluded);

  var _React$useState = React.useState(defaultCurrentPageIndex),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      currentState = _React$useState2[0],
      setCurrentState = _React$useState2[1]; // If a `currentPageIndex` has been specified, this renders as a controlled component


  var isControlled = typeof currentPageIndex !== 'undefined';
  var selectedPageIndex = isControlled ? currentPageIndex : currentState;
  var prevPageIndex = Math.max(selectedPageIndex - 1, 0);
  var nextPageIndex = Math.min(selectedPageIndex + 1, totalPages - 1);
  var buttonState = {
    currentPageIndex: selectedPageIndex,
    totalPages: totalPages
  };
  var handlePageSelected = React.useCallback(function (index) {
    if (!isControlled) {
      setCurrentState(index);
    }

    if (onPageSelected) {
      onPageSelected(index);
    }
  }, [isControlled, onPageSelected]);
  var defaultPreviousButtonProps = {
    pageIndex: prevPageIndex,
    rel: 'prev',
    title: 'Previous page',
    disabled: selectedPageIndex === 0,
    onClick: function onClick() {
      return handlePageSelected(prevPageIndex);
    },
    children: /*#__PURE__*/React.createElement(IconChevronLeft, {
      size: "xs"
    })
  };
  var defaultNextButtonProps = {
    pageIndex: nextPageIndex,
    rel: 'next',
    title: 'Next page',
    disabled: selectedPageIndex === totalPages - 1,
    onClick: function onClick() {
      return handlePageSelected(nextPageIndex);
    },
    children: /*#__PURE__*/React.createElement(IconChevronRight, {
      size: "xs"
    })
  };
  var controls = [/*#__PURE__*/React.createElement(PaginationJumpItem, {
    key: "previous"
  }, renderPreviousButton(defaultPreviousButtonProps, buttonState))];
  var indexRange = getIndexRange(selectedPageIndex, props.totalPages);
  indexRange.forEach(function (pageIndex) {
    var defaultPageNumberButtonProps = {
      pageIndex: pageIndex,
      title: "Page " + (pageIndex + 1) + (pageIndex === selectedPageIndex ? ', current page' : ''),
      pressed: selectedPageIndex === pageIndex,
      disabled: selectedPageIndex === pageIndex,
      onClick: function onClick() {
        return handlePageSelected(pageIndex);
      },
      children: pageIndex + 1
    };
    controls.push( /*#__PURE__*/React.createElement(PaginationNumberItem, {
      key: "" + getKey(),
      "aria-current": pageIndex === selectedPageIndex ? 'page' : null
    }, renderNumberButton(defaultPageNumberButtonProps, buttonState)));
  });
  controls.push( /*#__PURE__*/React.createElement(PaginationReadoutItem, {
    "aria-current": "page",
    key: "current"
  }, /*#__PURE__*/React.createElement(StyledText, {
    fontType: "body",
    key: "selectedPage"
  }, "Page ", selectedPageIndex + 1, " of ", totalPages)));
  controls.push( /*#__PURE__*/React.createElement(PaginationJumpItem, {
    key: "next"
  }, renderNextButton(defaultNextButtonProps, buttonState)));
  return /*#__PURE__*/React.createElement(StyledPagination, _extends({
    role: "navigation",
    "aria-label": "" + props['aria-label']
  }, rest), /*#__PURE__*/React.createElement(PaginationList, null, controls));
};

Pagination.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * (Controlled component) The index of the currently-selected page.
   * Including this prop will render this as a controlled component.
   * To render an uncontrolled component, use `defaultCurrentPageIndex` instead.
   */
  'currentPageIndex': PropTypes.number,

  /**
   * (Uncontrolled component) The index of the currently-selected page.
   * Including this prop will render this as an uncontrolled component.
   * To render a controlled component, use `currentPageIndex` instead.
   */
  'defaultCurrentPageIndex': PropTypes.number,

  /**
   * The total number of available pages
   * */
  'totalPages': PropTypes.number.isRequired,

  /**
   * An event handler to attach to page selection events. Takes one argument:
   *     `pageIndex`: the newly selected page index
   */
  'onPageSelected': PropTypes.func,

  /**
   * A render function for the page number buttons. Takes two arguments:
   *      `props`: [`PaginationButton`](#/More%20components?id=paginationbutton) props object
   *      `buttonState`: object containing `currentPageIndex` and `totalPages`
   * */
  'renderNumberButton': PropTypes.func,

  /**
   * A render function for the "previous page" button. Takes two arguments:
   *      `props`: [`PaginationButton`](#/More%20components?id=paginationbutton) props object
   *      `buttonState`: object containing `currentPageIndex` and `totalPages`
   * */
  'renderPreviousButton': PropTypes.func,

  /**
   * A render function for the "next page" button. Takes two arguments:
   *      `props`: [`PaginationButton`](#/More%20components?id=paginationbutton) props object
   *      `buttonState`: object containing `currentPageIndex` and `totalPages`
   * */
  'renderNextButton': PropTypes.func,

  /**
   * A unique label that describes this component's purpose.
   * Especially necessary when using multiple pagination components on the same page.
   * */
  'aria-label': PropTypes.string,

  /**
   * When true, the wider version of the pagination component will be rendered,
   * providing numbered buttons that link directly to pages.
   *
   * Supports [inline media query objects](#/Media%20queries).
   */
  'showNumberButtons': CustomPropTypes.inlineMediaQuery(PropTypes.bool),

  /**
   * @deprecated Use [inline media query objects](#/Media%20queries)
   */
  'mediaQuery': CustomPropTypes.mediaQuery
} : {};
Pagination.defaultProps = {
  'renderNumberButton': function renderNumberButton(props) {
    return /*#__PURE__*/React.createElement(PaginationButton, props);
  },
  'renderPreviousButton': function renderPreviousButton(props) {
    return /*#__PURE__*/React.createElement(PaginationButton, props);
  },
  'renderNextButton': function renderNextButton(props) {
    return /*#__PURE__*/React.createElement(PaginationButton, props);
  },
  'onPageSelected': null,
  'aria-label': 'Pagination',
  'showNumberButtons': {
    xs: false,
    md: true
  }
};
/** @component */

export default Pagination;